import { MainLayout } from '@/layout/MainLayout';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, Stats } from '@react-three/drei';
import { Suspense, useState, Component, ErrorInfo, ReactNode } from 'react';
import WuYuanWanModel from '../features/ship-3d/components/WuYuanWanModel';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from 'lucide-react';

// 加载指示器组件
const LoadingIndicator = ({ progress }: { progress: number }) => (
  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64">
    <div className="space-y-4 text-center">
      <Progress value={progress} className="w-full" />
      <p className="text-sm text-muted-foreground">
        加载中... {progress.toFixed(0)}%
      </p>
    </div>
  </div>
);

// 错误提示组件
const ErrorAlert = ({ message }: { message: string }) => (
  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96">
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>加载错误</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  </div>
);

// 错误边界组件
interface ErrorBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("3D渲染错误:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const Ship3DView = () => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  return (
    <MainLayout>
      <div className="relative w-full h-screen bg-background">
        {/* 3D场景 */}
        <Canvas
          camera={{ position: [0, 2, 5], fov: 75 }}
          shadows
          gl={{
            antialias: true,
            powerPreference: "high-performance",
            alpha: false,
            stencil: false,
            depth: true,
            preserveDrawingBuffer: true
          }}
          dpr={[1, 2]} // 限制最大像素比为2
          performance={{
            min: 0.5 // 允许降低渲染质量以保持性能
          }}
        >
          {/* 开发工具 */}
          <Stats />

          {/* 环境光设置 */}
          <ambientLight intensity={0.8} />
          <directionalLight
            position={[5, 5, 5]}
            intensity={1.2}
            castShadow
            shadow-mapSize={[1024, 1024]}
          />
          {/* 添加补充光源以增强环境光效果 */}
          <hemisphereLight
            intensity={0.5}
            groundColor="#404040"
          />
          <pointLight
            position={[-5, 5, -5]}
            intensity={0.5}
            color="#ffffff"
          />

          {/* 轨道控制器 */}
          <OrbitControls
            enableDamping
            dampingFactor={0.05}
            minDistance={2}
            maxDistance={10}
            minPolarAngle={Math.PI / 4}
            maxPolarAngle={Math.PI / 2}
          />

          {/* 模型加载 */}
          <ErrorBoundary fallback={<ErrorAlert message="3D模型加载失败，请刷新页面重试" />}>
            <Suspense fallback={<LoadingIndicator progress={loadingProgress} />}>
              <WuYuanWanModel
                onLoadProgress={setLoadingProgress}
                onError={(err) => {
                  console.error('模型加载错误:', err);
                  setError(err.message);
                }}
                enableAnimation
                materialConfig={{
                  roughness: 0.5, // 降低粗糙度
                  metalness: 0.2, // 降低金属度
                  envMapIntensity: 1.0, // 降低环境贴图强度
                  clearcoat: 0.3, // 添加清漆效果
                  clearcoatRoughness: 0.2
                }}
              />
            </Suspense>
          </ErrorBoundary>
        </Canvas>

        {/* 加载进度指示器 */}
        {loadingProgress < 100 && !error && (
          <LoadingIndicator progress={loadingProgress} />
        )}

        {/* 错误提示 */}
        {error && <ErrorAlert message={error} />}
      </div>
    </MainLayout>
  );
};

export default Ship3DView;
